import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useSession from '@/hooks/useSession';
import styles from './root-layout.module.scss';
import { createUserAuth } from '@/utils/session';
import { classnames } from '@/utils/functions';
import getSession from '@/api/users/getSession';
import ClarityScript from '@/components/ClarityScript';
import { gaIdentifyUser } from '@/utils/analytics';
import LoadingSpinner from '@/components/LoadingSpinner';
import Logo from '@/components/Logo';
import useTheme from '@/hooks/useTheme';
import { Slide, toast, ToastContainer } from 'react-toastify';

const RootLayout = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { session, setSession } = useSession();
  const { resolvedTheme } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const publicURLs = useRef<string[]>([
    '/',
    '/sign-up',
    '/privacy',
    '/terms-of-use',
    '/status',
    '/confirmUser',
  ]);

  // Before paint: otherwise the sign-in form renders once, is replaced by the
  // spinner, then mounts again empty, losing anything typed in between
  useLayoutEffect(() => {
    if (pathname === '/sign-in') {
      setIsLoading(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (publicURLs.current.includes(pathname)) {
      setIsLoading(false);
      return;
    } else if (import.meta.env.VITE_MAINTENANCE_END) {
      navigate('/status', { replace: true });
      return;
    } else if (session && session.email) {
      setIsLoading(false);
      return;
    } else {
      const checkIfLoggedIn = async () => {
        try {
          const newSession = await getSession();

          if (!newSession) {
            setIsLoading(false);
          } else {
            const { email, sub } = newSession;

            const tmpAuth = await createUserAuth(email, sub);
            setSession(tmpAuth);
            gaIdentifyUser(tmpAuth.sub);

            if (tmpAuth.pendingPolicies.length > 0) {
              navigate('/policy-review');
            } else if (pathname === '/sign-in') {
              navigate('/', { replace: true });
            }
            setIsLoading(false);
          }
        } catch (err) {
          console.error(err);
          setIsLoading(false);
        }
      };
      checkIfLoggedIn();
    }
  }, [session, navigate, pathname, setSession]);

  useEffect(() => {
    toast.dismiss();
  }, [pathname]);

  return (
    <main
      data-loading={isLoading}
      className={classnames(styles.root, { [styles['loading']]: isLoading })}
    >
      <ClarityScript />
      {isLoading ? (
        <div className={styles['loading-wrapper']}>
          <div className={styles['loading-logo-plate']}>
            <Logo
              className={styles['loading-logo']}
              label="Gannet is loading"
            />
          </div>
          <LoadingSpinner />
        </div>
      ) : (
        <Outlet />
      )}
      <ToastContainer
        className={styles['toasty']}
        theme={resolvedTheme}
        position="bottom-right"
        pauseOnHover={true}
        autoClose={4000}
        transition={Slide}
      />
    </main>
  );
};

export default RootLayout;
